import * as React from "react";

import { cn } from "@/lib/utils";

import { LEN_TOOLTIP_DELAY_MS, MuiTooltip } from "./mui-tooltip";

// A disabled control that can still explain itself — `@modsy/react-ui`'s
// `Button` at `disabled` + `disabledTooltip` (`button.tsx:103-119`), and the
// create-derived button's `<div>` wrap (`spec-create-button.tsx:76-97`).
//
// A disabled `<button>` receives no pointer events and no focus, so a tooltip
// hung on it never fires. The donor's answer is a wrapper: `react-ui`'s
// `Tooltip` at `showCallout` (MUI's `arrow`, `enterDelay` 0 —
// `tooltip.tsx:18-21,76-80`) around a `<span>` around the disabled control. The
// span, not the control, is what the pointer lands on.
//
// **The span also takes the keyboard**, which the donor's does not — a named
// parity break the owner ruled for (#698's decision round, D6). The donor's
// span is not focusable and the disabled control inside it cannot be, so a
// keyboard user in modsy never reaches the explanation. The waiting affordance
// exists to say *why* a visible control does nothing (#678 Q1), and the ticket
// asks for that on focus as on hover (#690, stories 45-46), so the span is a
// tab stop, opens the tooltip on focus, and carries the sentence as its
// accessible description.
//
// Two exports, one mechanism:
//
//   - `DisabledTooltip` takes its sentence — the create-derived button's
//     "Specification must be published…" (#691) rides this;
//   - `WaitingAffordance` takes none: it is `DisabledTooltip` at the one shared
//     sentence, so no screen spells it.

/**
 * The waiting affordance's one sentence. Agent-chosen (#690, Further Notes): the
 * glossary fixes the meaning — the verb ships from a later node — and this
 * constant is the one place to change the words.
 */
export const WAITING_AFFORDANCE_TITLE = "Not yet available in Atlas";

/**
 * The disabled link — **the one look here the source does not have**, because
 * the source has no disabled link anywhere (#679 Q3 asks for a link-styled,
 * disabled affordance). MUI's `Link` markup (`underline='always'`) in
 * `text.disabled`, with the underline at the link's own 0.4 over that ink
 * (`getTextDecoration.js`) and the plain arrow `ButtonBase` shows when
 * disabled.
 */
const DISABLED_LINK =
  "text-lennar-ink-disabled decoration-lennar-ink-disabled-underline cursor-default underline";

interface DisabledTooltipProps {
  /** The sentence the tooltip shows and the control is described by. */
  title: string;
  /**
   * What is disabled.
   *
   *   - `control` (the default): the child is a button — `MuiButton`,
   *     `IconButton` — and is handed `disabled`, so it draws the ported
   *     component's own disabled look (the theme's 0.5 opacity on a button,
   *     `action.disabled` on an icon button);
   *   - `link`: the child is the link's *text*, drawn as a disabled link.
   */
  shape?: "control" | "link";
  /** react-ui `Tooltip`'s `maxWidth` — 200 on the Spec list's row buttons. */
  maxWidthPx?: number;
  children?: React.ReactNode;
  className?: string;
}

export function DisabledTooltip({
  title,
  shape = "control",
  maxWidthPx,
  children,
  className,
}: DisabledTooltipProps) {
  const descriptionId = React.useId();

  const inert =
    shape === "control" ? (
      React.isValidElement<{ disabled?: boolean }>(children) ? (
        React.cloneElement(children, { disabled: true })
      ) : (
        children
      )
    ) : (
      // A link with nowhere to go: no `href`, so it is not a link to the
      // browser either, and `aria-disabled` for assistive tech.
      <a data-slot="disabled-link" aria-disabled="true" className={DISABLED_LINK}>
        {children}
      </a>
    );

  return (
    <MuiTooltip title={title} delayMs={LEN_TOOLTIP_DELAY_MS} maxWidthPx={maxWidthPx} arrow asChild>
      <span
        data-slot="disabled-tooltip"
        tabIndex={0}
        aria-describedby={descriptionId}
        className={cn(
          "focus-visible:outline-lennar-blue-600 inline-flex outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid",
          className,
        )}
      >
        {inert}
        <span id={descriptionId} hidden>
          {title}
        </span>
      </span>
    </MuiTooltip>
  );
}

type WaitingAffordanceProps = Omit<DisabledTooltipProps, "title">;

/**
 * A control whose verb ships from a later map node: present, disabled, and
 * saying so in the one shared sentence (#678 Q1).
 */
export function WaitingAffordance(props: WaitingAffordanceProps) {
  return <DisabledTooltip title={WAITING_AFFORDANCE_TITLE} {...props} />;
}
